import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Search, Visibility } from '@mui/icons-material';
import { DropdownItem } from '../../../types';
import { attendanceApi } from '../../../api/attendanceApi';
import { employeeApi } from '../../../api/employeeApi';
import { toolsApi, EmployeeOption as ToolEmployeeOption } from '../../../api/toolsApi';
import { RootState } from '../../../store/store';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

export interface EmployeeOption {
  id: string;
  label: string;
}

interface AttendanceReportFilterFormProps {
  singleDate?: string;
  onSingleDateChange?: (value: string) => void;
  department?: string;
  onDepartmentChange?: (value: string) => void;
  employee?: string;
  onEmployeeChange?: (value: string) => void;
  status?: string;
  onStatusChange?: (value: string) => void;
  departments?: DropdownItem[];
  employees?: EmployeeOption[];
  lockEmployee?: boolean;
  statusOptions?: string[];
  loading?: boolean;
  onGetData?: () => void;
}

interface ReportSummary {
  totalEmployees: number;
  presentCount: number;
  halfDayCount: number;
  absentCount: number;
  leaveCount: number;
  lateCount: number;
  avgWorkingHours: string;
}

interface ReportRow {
  attendanceId?: string;
  employeeId?: string;
  date: string;
  employeeName: string;
  employeeCode: string;
  department: string;
  checkInTime: string;
  checkOutTime: string;
  workedHours: string;
  attendanceStatus: string;
  presentDays: number;
  halfDayDays: number;
  absentDays: number;
  leaveDays: number;
  lateDays: number;
  totalWorkingHours: string;
  avgDailyHours: string;
  overtimeHours?: number;
  isOvertimeEligible?: boolean;
  overtimeRequestStatus?: string;
  compOffRequestStatus?: string;
  attendanceRegularizationStatus?: string;
  needsRegularization?: boolean;
  isOnApprovedLeave?: boolean;
  isHalfDayLeave?: boolean;
  isHoliday?: boolean;
  isCompOffEligible?: boolean;
  isLateCheckIn?: boolean;
  isEarlyCheckOut?: boolean;
  isMissingPunch?: boolean;
}

const defaultStatusOptions = ['Present', 'Absent', 'HalfDay', 'Leave', 'Week Off'];

const defaultSummary: ReportSummary = {
  totalEmployees: 0,
  presentCount: 0,
  halfDayCount: 0,
  absentCount: 0,
  leaveCount: 0,
  lateCount: 0,
  avgWorkingHours: '0h 0m',
};

const getFirstDefined = (...values: any[]) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const toNumber = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeStatusText = (value: any): string => String(value || '').trim().toLowerCase();

const isWeekOffStatus = (status: string): boolean => {
  const normalized = status.replace(/[\s-]/g, '');
  return normalized === 'weekoff' || normalized === 'weeklyoff';
};

interface AttendanceDetailRow {
  checkInTime?: string | null;
  checkOutTime?: string | null;
  totalHours?: number | string | null;
}

const normalizeReportRows = (rawData: any[]): ReportRow[] => {
  const rows = Array.isArray(rawData) ? rawData : [];

  return rows.map((item) => ({
    date: String(getFirstDefined(item?.date, item?.Date, '') || ''),
    attendanceId: getFirstDefined(item?.attendanceId, item?.AttendanceId),
    employeeId: getFirstDefined(item?.employeeId, item?.EmployeeId),
    employeeName: getFirstDefined(item?.employeeName, item?.EmployeeName, 'Unknown'),
    employeeCode: getFirstDefined(item?.employeeCode, item?.EmployeeCode, '-'),
    department: getFirstDefined(item?.department, item?.Department, '-'),
    checkInTime: String(getFirstDefined(item?.checkInTime, item?.CheckInTime, '')),
    checkOutTime: String(getFirstDefined(item?.checkOutTime, item?.CheckOutTime, '')),
    workedHours: String(getFirstDefined(item?.workedHours, item?.WorkedHours, item?.totalWorkingHours, item?.TotalWorkingHours, '0:00')),
    attendanceStatus: String(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, 'Absent')),
    presentDays: toNumber(
      getFirstDefined(
        item?.presentDays,
        item?.PresentDays,
        (() => {
          const status = normalizeStatusText(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, ''));
          return !isWeekOffStatus(status) && (status.includes('present') || status.includes('half')) ? 1 : 0;
        })()
      ),
      0
    ),
    halfDayDays: toNumber(
      getFirstDefined(
        item?.halfDayDays,
        item?.HalfDayDays,
        normalizeStatusText(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, '')).includes('half') ? 1 : 0
      ),
      0
    ),
    absentDays: toNumber(
      getFirstDefined(
        item?.absentDays,
        item?.AbsentDays,
        normalizeStatusText(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, '')).includes('absent') ? 1 : 0
      ),
      0
    ),
    leaveDays: toNumber(
      getFirstDefined(
        item?.leaveDays,
        item?.LeaveDays,
        normalizeStatusText(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, '')).includes('leave') ? 1 : 0
      ),
      0
    ),
    lateDays: toNumber(
      getFirstDefined(
        item?.lateDays,
        item?.LateDays,
        normalizeStatusText(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, '')).includes('late') ? 1 : 0
      ),
      0
    ),
    totalWorkingHours: String(getFirstDefined(item?.totalWorkingHours, item?.TotalWorkingHours, '0h 0m')),
    avgDailyHours: String(getFirstDefined(item?.avgDailyHours, item?.AvgDailyHours, '0h 0m')),
    overtimeHours: toNumber(getFirstDefined(item?.overtimeHours, item?.OvertimeHours), 0),
    isOvertimeEligible: Boolean(getFirstDefined(item?.isOvertimeEligible, item?.IsOvertimeEligible, false)),
    overtimeRequestStatus: String(getFirstDefined(item?.overtimeRequestStatus, item?.OvertimeRequestStatus, '')),
    compOffRequestStatus: String(getFirstDefined(item?.compOffRequestStatus, item?.CompOffRequestStatus, '')),
    attendanceRegularizationStatus: String(getFirstDefined(item?.attendanceRegularizationStatus, item?.AttendanceRegularizationStatus, '')),
    needsRegularization: Boolean(getFirstDefined(item?.needsRegularization, item?.NeedsRegularization, false)),
    isOnApprovedLeave: Boolean(getFirstDefined(item?.isOnApprovedLeave, item?.IsOnApprovedLeave, false)),
    isHoliday: Boolean(getFirstDefined(item?.isHoliday, item?.IsHoliday, false)),
    isCompOffEligible: Boolean(getFirstDefined(item?.isCompOffEligible, item?.IsCompOffEligible, false)),
    isLateCheckIn: Boolean(getFirstDefined(item?.isLateCheckIn, item?.IsLateCheckIn, false)),
    isEarlyCheckOut: Boolean(getFirstDefined(item?.isEarlyCheckOut, item?.IsEarlyCheckOut, false)),
    isMissingPunch: Boolean(getFirstDefined(item?.isMissingPunch, item?.IsMissingPunch, false)),
  }));
};

const normalizeSummary = (rawSummary: any, rows: ReportRow[]): ReportSummary => ({
  totalEmployees: toNumber(getFirstDefined(rawSummary?.totalEmployees, rawSummary?.TotalEmployees), rows.length),
  presentCount: toNumber(getFirstDefined(rawSummary?.presentCount, rawSummary?.PresentCount), rows.reduce((sum, r) => sum + r.presentDays, 0)),
  halfDayCount: toNumber(getFirstDefined(rawSummary?.halfDayCount, rawSummary?.HalfDayCount), rows.reduce((sum, r) => sum + r.halfDayDays, 0)),
  absentCount: toNumber(getFirstDefined(rawSummary?.absentCount, rawSummary?.AbsentCount), rows.reduce((sum, r) => sum + r.absentDays, 0)),
  leaveCount: toNumber(getFirstDefined(rawSummary?.leaveCount, rawSummary?.LeaveCount), rows.reduce((sum, r) => sum + r.leaveDays, 0)),
  lateCount: toNumber(getFirstDefined(rawSummary?.lateCount, rawSummary?.LateCount), rows.reduce((sum, r) => sum + r.lateDays, 0)),
  avgWorkingHours: String(getFirstDefined(rawSummary?.avgWorkingHours, rawSummary?.AvgWorkingHours, '0h 0m')),
});

const normalizeAttendanceLogRowsForRegularization = (rawData: any[], employeeId: string): ReportRow[] => {
  const rows = Array.isArray(rawData) ? rawData : [];

  return rows.map((item) => {
    const apiStatus = String(getFirstDefined(item?.attendanceStatus, item?.AttendanceStatus, item?.status, item?.Status, 'Absent'));
    const checkInTime = String(getFirstDefined(item?.checkInTime, item?.CheckInTime, ''));
    const checkOutTime = String(getFirstDefined(item?.checkOutTime, item?.CheckOutTime, ''));
    const isHalfDayLeave = Boolean(getFirstDefined(item?.isHalfDayLeave, item?.IsHalfDayLeave, false));
    // A half-day leave with no punches leaves the other half unaccounted for (payroll treats it as
    // absent), so it is not a plain "Leave" day.
    const attendanceStatus = isHalfDayLeave && normalizeStatusText(apiStatus) === 'leave'
      ? 'Half Day Leave'
      : apiStatus;
    const statusLower = normalizeStatusText(attendanceStatus);
    const isWeekOff = isWeekOffStatus(statusLower);
    // Exactly one punch present. A day with no punches at all (leave, holiday) is not a missed punch.
    const isMissingPunch = !isWeekOff && !statusLower.includes('absent') && Boolean(checkInTime) !== Boolean(checkOutTime);
    // Worked half + half-day leave makes a full day - nothing left to regularize.
    const isHalfDayCoveredByLeave = isHalfDayLeave && statusLower.includes('half') && !statusLower.includes('leave');
    const isLateCheckIn = Boolean(getFirstDefined(item?.isLate, item?.IsLate, false));
    const isOvertimeEligible = Boolean(getFirstDefined(item?.isOvertimeEligible, item?.IsOvertimeEligible, false));
    const isCompOffEligible = Boolean(getFirstDefined(item?.isCompOffEligible, item?.IsCompOffEligible, false));
    const needsRegularization =
      statusLower.includes('absent') ||
      (statusLower.includes('half') && !isHalfDayCoveredByLeave) ||
      isLateCheckIn ||
      isMissingPunch ||
      isOvertimeEligible ||
      isCompOffEligible;

    return {
      date: String(getFirstDefined(item?.date, item?.Date, '') || ''),
      attendanceId: getFirstDefined(item?.attendanceId, item?.AttendanceId),
      employeeId,
      employeeName: String(getFirstDefined(item?.employeeName, item?.EmployeeName, 'Unknown')),
      employeeCode: String(getFirstDefined(item?.employeeCode, item?.EmployeeCode, '-')),
      department: String(getFirstDefined(item?.department, item?.Department, '-')),
      checkInTime,
      checkOutTime,
      workedHours: String(getFirstDefined(item?.time, item?.Time, '0:00')),
      attendanceStatus,
      presentDays: !isWeekOff && (statusLower.includes('present') || statusLower.includes('late') || (statusLower.includes('half') && !statusLower.includes('leave'))) ? 1 : 0,
      halfDayDays: statusLower.includes('half') && !statusLower.includes('leave') ? 1 : 0,
      absentDays: statusLower.includes('absent') ? 1 : 0,
      leaveDays: statusLower.includes('leave') ? 1 : 0,
      lateDays: isLateCheckIn ? 1 : 0,
      totalWorkingHours: String(getFirstDefined(item?.time, item?.Time, '0h 0m')),
      avgDailyHours: String(getFirstDefined(item?.time, item?.Time, '0h 0m')),
      attendanceRegularizationStatus: '',
      needsRegularization,
      isOnApprovedLeave: statusLower.includes('leave') && !isHalfDayLeave,
      isHalfDayLeave,
      isHoliday: Boolean(getFirstDefined(item?.isHoliday, item?.IsHoliday, false)),
      isLateCheckIn,
      isMissingPunch,
      isEarlyCheckOut: false,
      isOvertimeEligible,
      isCompOffEligible,
      compOffRequestStatus: String(getFirstDefined(item?.compOffRequestStatus, item?.CompOffRequestStatus, '')),
      overtimeHours: toNumber(getFirstDefined(item?.overtimeHours, item?.OvertimeHours), 0),
    };
  });
};

const normalizeDateKey = (value?: string): string => {
  if (!value) return '';

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split('-');
    return `${year}-${month}-${day}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.slice(0, 10);
  }

  return parsed.toISOString().slice(0, 10);
};

const derivePunchTimes = (punchRows: any[], date?: string) => {
  const rows = Array.isArray(punchRows) ? punchRows : [];
  const dateKey = normalizeDateKey(date);
  const filtered = dateKey ? rows.filter((p) => normalizeDateKey(String(p?.date || '')) === dateKey) : rows;

  const normalizePunchType = (value: any) => String(value || '').toLowerCase().replace(/[^a-z]/g, '');

  const checkIns = filtered
    .filter((p) => normalizePunchType(p?.type) === 'checkin')
    .map((p) => p?.time)
    .filter(Boolean);

  const checkOuts = filtered
    .filter((p) => normalizePunchType(p?.type) === 'checkout')
    .map((p) => p?.time)
    .filter(Boolean);

  return {
    checkIn: checkIns.length > 0 ? String(checkIns[0]) : '',
    checkOut: checkOuts.length > 0 ? String(checkOuts[checkOuts.length - 1]) : '',
  };
};

const formatAttendanceDate = (date: string): string => {
  if (!date) return '-';

  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    const [day, month, year] = date.split('-');
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// const formatAttendanceTime = (value?: string | null): string => {
//   if (!value) return '';

//   if (/^\d{2}:\d{2}$/.test(value)) {
//     return value;
//   }

//   const parsed = new Date(value);
//   if (Number.isNaN(parsed.getTime())) return value;

//   return parsed.toLocaleTimeString('en-GB', {
//     hour: '2-digit',
//     minute: '2-digit',
//     hour12: false,
//   });
// };

// const formatAttendanceTime = (value?: string | null): string => {
//   if (!value) return '';

//   // Already HH:mm
//   if (/^\d{2}:\d{2}$/.test(value)) return value;

//   // Already 12-hour format like "10:16 AM"
//   if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(value)) return value;

//   // Strip the Z so browser treats it as LOCAL time, not UTC
//   // (backend already converted to local before sending)
//   const normalized = value.endsWith('Z')
//     ? value.slice(0, -1)        // remove Z → treat as local
//     : value;

//   const parsed = new Date(normalized);
//   if (Number.isNaN(parsed.getTime())) return value;

//   return parsed.toLocaleTimeString('en-US', {
//     hour: '2-digit',
//     minute: '2-digit',
//     hour12: true,
//   });
// };

const formatAttendanceTime = (value?: string | null): string => {
  if (!value) return '';

  if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(value)) return value;

  const wallClockMatch = value.match(/(?:^|T|\s)(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?/);
  if (wallClockMatch) {
    const hour = Number(wallClockMatch[1]);
    const minute = Number(wallClockMatch[2]);

    if (Number.isInteger(hour) && Number.isInteger(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
    }
  }

  // Already HH:mm
  if (/^\d{2}:\d{2}$/.test(value)) return value;

  // Already 12-hour format like "10:16 AM"
  if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(value)) return value;

  // Handle both:
  // - "2026-06-05T10:10:41" (no Z — already IST from backend)
  // - "2026-06-05T10:10:41Z" (UTC — strip Z so browser doesn't shift it)
  // - "2026-06-05T10:10:41.857+05:30" (with offset)
  let normalized = value;

  // If it has a timezone offset (+05:30), parse as-is
  if (/[+-]\d{2}:\d{2}$/.test(value)) {
    // Has explicit offset — parse directly
    normalized = value;
  } else if (value.endsWith('Z')) {
    // UTC marker — strip it so browser treats as local (backend already converted to IST)
    normalized = value.slice(0, -1);
  }
  // No Z, no offset → treat as local already (yyyy-MM-ddTHH:mm:ss)

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatWorkedHours = (value?: number | string | null): string => {
  if (value === undefined || value === null || value === '') return '0:00';

  if (typeof value === 'string') {
    return value;
  }

  const totalMinutes = Math.max(0, Math.round(value * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

type Meridiem = 'AM' | 'PM';

const toTwentyFourHourTime = (time: string, meridiem: Meridiem): string | null => {
  const match = time.trim().match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) {
    return null;
  }

  const normalizedHour = meridiem === 'AM'
    ? (hour === 12 ? 0 : hour)
    : (hour === 12 ? 12 : hour + 12);

  return `${String(normalizedHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const normalizeMeridiemTimeInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 0) return '';
  if (digits.length === 1) {
    return Number(digits) <= 1 ? digits : '';
  }
  if (digits.length === 2) {
    const hour = Number(digits);
    return hour >= 1 && hour <= 12 ? digits : digits[0];
  }

  const hour = Number(digits.slice(0, 2));
  const minutePrefix = digits[2];
  if (hour < 1 || hour > 12 || Number(minutePrefix) > 5) {
    return digits.slice(0, 2);
  }

  if (digits.length === 3) return `${digits.slice(0, 2)}:${minutePrefix}`;

  const minute = Number(digits.slice(2));
  return minute <= 59 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : `${digits.slice(0, 2)}:${minutePrefix}`;
};

const getStatusChipColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const normalized = normalizeStatusText(status);
  if (normalized.includes('present')) return 'success';
  if (normalized.includes('absent')) return 'error';
  if (normalized.includes('late')) return 'warning';
  if (normalized.includes('leave') || isWeekOffStatus(normalized)) return 'info';
  return 'default';
};

const AttendanceReportFilterForm: React.FC<AttendanceReportFilterFormProps> = ({
  singleDate,
  onSingleDateChange,
  department,
  onDepartmentChange,
  employee,
  onEmployeeChange,
  status,
  onStatusChange,
  departments,
  employees,
  lockEmployee = false,
  statusOptions,
  loading,
  onGetData,
}) => {
  const isEmbeddedMode = Boolean(onGetData && onSingleDateChange && onDepartmentChange && onEmployeeChange && onStatusChange);
  const user = useSelector((state: RootState) => state.auth.user);
  const userRoles = [...(user?.roles ?? []), ...(user?.roleName ? [user.roleName] : [])];
  const normalizedRoles = userRoles.map((role) => role.toLowerCase().replace(/[\s_-]/g, ''));
  const canRegularizeForEmployees = normalizedRoles.some((role) =>
    role.includes('admin') ||
    role === 'hr' ||
    role.includes('humanresources')
  );

  const [localFromDate, setLocalFromDate] = useState('');
  const [localToDate, setLocalToDate] = useState('');
  const [localStatus, setLocalStatus] = useState('');
  const [currentEmployee, setCurrentEmployee] = useState<EmployeeOption | null>(null);
  const [adminEmployees, setAdminEmployees] = useState<ToolEmployeeOption[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ReportSummary>(defaultSummary);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [generated, setGenerated] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [regularizationOpen, setRegularizationOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReportRow | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [requestViewFilter, setRequestViewFilter] = useState<'All' | 'Attendance' | 'Overtime' | 'CompOff'>('All');
  const [punchesLoading, setPunchesLoading] = useState(false);
  const [punches, setPunches] = useState<any[]>([]);
  const [selectedAttendance, setSelectedAttendance] = useState<AttendanceDetailRow | null>(null);
  const [regularizationFor, setRegularizationFor] = useState<'Both' | 'Check In' | 'Check Out'>('Both');
  const [regularizationRequestType, setRegularizationRequestType] = useState<'Attendance' | 'Overtime'>('Attendance');
  const [regularizationDate, setRegularizationDate] = useState('');
  const [regularizationCheckIn, setRegularizationCheckIn] = useState('');
  const [regularizationCheckInMeridiem, setRegularizationCheckInMeridiem] = useState<Meridiem>('AM');
  const [regularizationCheckOut, setRegularizationCheckOut] = useState('');
  const [regularizationCheckOutMeridiem, setRegularizationCheckOutMeridiem] = useState<Meridiem>('PM');
  const [regularizationOtHours, setRegularizationOtHours] = useState('');
  const [regularizationReason, setRegularizationReason] = useState('');
  const [employeeJoinedDate, setEmployeeJoinedDate] = useState<string>('');

  // Comp-Off has its own lightweight flow (reason only).
  const [compOffOpen, setCompOffOpen] = useState(false);
  const [compOffRow, setCompOffRow] = useState<ReportRow | null>(null);
  const [compOffReason, setCompOffReason] = useState('');
  const [compOffSubmitting, setCompOffSubmitting] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const effectiveStatusOptions = statusOptions ?? defaultStatusOptions;

  useEffect(() => {
    if (isEmbeddedMode) return;

    employeeApi
      .getCurrentEmployee()
      .then((me) => {
        setCurrentEmployee({
          id: String(me?.id || ''),
          label: `${String(me?.fullName || 'Employee')} (${String(me?.employeeCode || '-')})`,
        });
        if (me?.joinedDate) {
          setEmployeeJoinedDate(String(me.joinedDate).slice(0, 10));
        }
      })
      .catch(() => setCurrentEmployee(null));
  }, [isEmbeddedMode]);

  useEffect(() => {
    if (isEmbeddedMode || !canRegularizeForEmployees) return;

    toolsApi
      .getEmployees()
      .then((result) => {
        const sortedEmployees = [...result].sort((a, b) => {
          const aLabel = `${a.name || ''} ${a.employeeCode || ''}`.trim();
          const bLabel = `${b.name || ''} ${b.employeeCode || ''}`.trim();
          return aLabel.localeCompare(bLabel);
        });

        setAdminEmployees(sortedEmployees);
      })
      .catch(() => {
        setAdminEmployees([]);
        toast.error('Failed to load employees');
      });
  }, [canRegularizeForEmployees, isEmbeddedMode]);

  const loadEmployeePunches = async (employeeId?: string, startDate?: string, endDate?: string) => {
    if (!employeeId) {
      setPunches([]);
      return;
    }

    setPunchesLoading(true);
    try {
      const result = await attendanceApi.getAttendanceRecords({
        employeeId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        pageNumber: 1,
        pageSize: 100,
      });
      setPunches(result?.data || []);
    } catch {
      setPunches([]);
    } finally {
      setPunchesLoading(false);
    }
  };

  const loadAttendanceDetail = async (employeeId?: string, date?: string) => {
    if (!employeeId || !date) {
      setSelectedAttendance(null);
      return;
    }

    try {
      const result = await attendanceApi.getByEmployee(employeeId, date, date) as any[];
      const match = Array.isArray(result) ? result.find((item) => normalizeDateKey(String(item?.date || item?.Date || '')) === date) : null;

      setSelectedAttendance(match ? {
        checkInTime: getFirstDefined(match?.checkInTime, match?.CheckInTime, null),
        checkOutTime: getFirstDefined(match?.checkOutTime, match?.CheckOutTime, null),
        totalHours: getFirstDefined(match?.totalHours, match?.TotalHours, null),
      } : null);
    } catch {
      setSelectedAttendance(null);
    }
  };

  const handleGenerate = async () => {
    const effectiveEmployeeId = canRegularizeForEmployees ? selectedEmployeeId : currentEmployee?.id;

    if (!effectiveEmployeeId) {
      setLocalError(canRegularizeForEmployees ? 'Please select an employee' : 'Logged-in employee not found');
      return;
    }

    // Date validation
    if (!localFromDate || !localToDate) {
      setLocalError('Please select both From Date and To Date');
      return;
    }

    setLocalLoading(true);
    setLocalError(null);

    try {
      const selectedEmployee = canRegularizeForEmployees
        ? adminEmployees.find((item) => item.id === effectiveEmployeeId)
        : null;
      const fallbackEmployeeName = selectedEmployee?.name || currentEmployee?.label.split(' (')[0] || 'Employee';
      const fallbackEmployeeCode = selectedEmployee?.employeeCode || (
        currentEmployee?.label.includes('(')
          ? currentEmployee.label.split('(')[1].replace(')', '')
          : '-'
      );

      const result = canRegularizeForEmployees
        ? await attendanceApi.getAttendanceRecords({
            employeeId: effectiveEmployeeId,
            startDate: localFromDate,
            endDate: localToDate,
            status: localStatus || undefined,
            pageNumber: 1,
            pageSize: 1000,
          })
        : await attendanceApi.getMyAttendanceReport({
            fromDate: localFromDate,
            toDate: localToDate,
            status: localStatus || undefined,
          });
      const apiResult = result as any;
      const rawData = getFirstDefined(apiResult?.data, apiResult?.Data, []);
      const rawSummary = getFirstDefined(apiResult?.summary, apiResult?.Summary, {});

      const normalizedRows = canRegularizeForEmployees
        ? normalizeAttendanceLogRowsForRegularization(rawData, effectiveEmployeeId)
        : normalizeReportRows(rawData);
      const normalizedSummary = normalizeSummary(rawSummary, normalizedRows);

      const finalRows =
        normalizedRows.length === 0 &&
        localFromDate &&
        localToDate &&
        localFromDate === localToDate
          ? [
              {
                date: localFromDate,
                employeeId: effectiveEmployeeId,
                employeeName: fallbackEmployeeName,
                employeeCode: fallbackEmployeeCode,
                department: '-',
                checkInTime: '',
                checkOutTime: '',
                workedHours: '0:00',
                attendanceStatus: 'Absent',
                presentDays: 0,
                halfDayDays: 0,
                absentDays: 1,
                leaveDays: 0,
                lateDays: 0,
                totalWorkingHours: '0h 0m',
                avgDailyHours: '0h 0m',
              },
            ]
          : normalizedRows;

      setRows(finalRows);
      setSummary(
        normalizedRows.length === 0 &&
          localFromDate &&
          localToDate &&
          localFromDate === localToDate
          ? {
              totalEmployees: 1,
              presentCount: 0,
              halfDayCount: 0,
              absentCount: 1,
              leaveCount: 0,
              lateCount: 0,
              avgWorkingHours: '0h 0m',
            }
          : normalizedSummary
      );

      setGenerated(true);
    } catch (err: any) {
      setLocalError(
        err?.response?.data?.message ||
        'Failed to get employee attendance data'
      );
      setRows([]);
      setSummary(defaultSummary);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleOpenDetails = async (row: ReportRow) => {
    const selectedDate = normalizeDateKey(row.date);
    setSelectedRow(row);
    setDetailTab(0);
    setDetailsOpen(true);
    await Promise.all([
      loadEmployeePunches(row.employeeId, selectedDate || undefined, selectedDate || undefined),
      loadAttendanceDetail(row.employeeId, selectedDate || undefined),
    ]);
  };

  const handleOpenRegularization = async (row: ReportRow, requestType: 'Attendance' | 'Overtime' = 'Attendance') => {
    if ((row.attendanceStatus || '').toLowerCase().includes('leave')) {
      toast.error('Regularization is not allowed for dates with approved leave.');
      return;
    }
    const selectedDate = normalizeDateKey(row.date);
    setSelectedRow(row);
    setRegularizationRequestType(requestType);
    setRegularizationFor('Both');
    setRegularizationDate(selectedDate || localFromDate || new Date().toISOString().slice(0, 10));
    setRegularizationCheckIn('');
    setRegularizationCheckInMeridiem('AM');
    setRegularizationCheckOut('');
    setRegularizationCheckOutMeridiem('PM');
    setRegularizationOtHours(requestType === 'Overtime' ? String(row.overtimeHours || '') : '');
    setRegularizationReason('');
    setRegularizationOpen(true);
    await Promise.all([
      loadEmployeePunches(row.employeeId, selectedDate || undefined, selectedDate || undefined),
      loadAttendanceDetail(row.employeeId, selectedDate || undefined),
    ]);
  };

  const handleOpenCompOff = (row: ReportRow) => {
    setCompOffRow(row);
    setCompOffReason('');
    setCompOffOpen(true);
  };

  const handleSubmitCompOff = async () => {
    if (!compOffRow) return;
    if (!compOffReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    const employeeId = compOffRow.employeeId || currentEmployee?.id;
    if (!employeeId) {
      toast.error('Employee not found for comp-off request');
      return;
    }
    setCompOffSubmitting(true);
    try {
      await attendanceApi.submitRegularizationRequest({
        employeeId,
        attendanceId: compOffRow.attendanceId,
        date: normalizeDateKey(compOffRow.date),
        requestType: 'CompOff',
        requestedCheckIn: null,
        requestedCheckOut: null,
        requestedOtHours: null,
        reason: compOffReason.trim(),
      });
      toast.success('Comp-off request submitted successfully');
      setCompOffOpen(false);
      handleGenerate();
    } catch (err: any) {
      const apiError = err?.response?.data;
      toast.error(apiError?.message || apiError?.title || 'Failed to submit comp-off request');
    } finally {
      setCompOffSubmitting(false);
    }
  };

  const canRequestAttendance = (row: ReportRow) => {
    // Can NOT request attendance regularization if:
    // - on approved leave
    // - is a holiday with no work
    if (row.isOnApprovedLeave) return false;
    if (row.isHoliday && !(row.checkInTime && row.checkOutTime)) return false;
    // CAN request if: absent, late checkin, early checkout, missing punch, half-day
    const statusLower = (row.attendanceStatus || '').toLowerCase();
    if (isWeekOffStatus(statusLower)) return false;
    return statusLower.includes('absent') || row.isLateCheckIn || row.isEarlyCheckOut || row.isMissingPunch || statusLower.includes('half');
  };
  const canRequestOvertime = (row: ReportRow) => {
    if (row.isOnApprovedLeave) return false;
    return Boolean(row.isOvertimeEligible);
  };
  const canRequestCompOff = (row: ReportRow) => Boolean(row.isCompOffEligible);

  const selectedRequestOptions = useMemo<Array<'Attendance' | 'Overtime'>>(() => {
    if (!selectedRow) return ['Attendance'];

    const options: Array<'Attendance' | 'Overtime'> = [];
    if (canRequestAttendance(selectedRow)) options.push('Attendance');
    if (canRequestOvertime(selectedRow)) options.push('Overtime');
    return options.length > 0 ? options : ['Attendance'];
  }, [selectedRow]);

  useEffect(() => {
    if (!regularizationOpen) return;

    if (!selectedRequestOptions.includes(regularizationRequestType)) {
      setRegularizationRequestType(selectedRequestOptions[0] ?? 'Attendance');
    }
  }, [regularizationOpen, regularizationRequestType, selectedRequestOptions]);

  const handleSubmitRegularization = async () => {
    if (!regularizationDate) {
      toast.error('Date is required');
      return;
    }
    if (!regularizationReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!selectedRequestOptions.includes(regularizationRequestType)) {
      toast.error('Selected request type is not available for this attendance record');
      return;
    }
    if (regularizationRequestType === 'Overtime') {
      const requestedOtHours = Number(regularizationOtHours);
      if (!requestedOtHours || requestedOtHours <= 0) {
        toast.error('OT hours are required');
        return;
      }
    } else {
      if (regularizationFor !== 'Check Out' && !regularizationCheckIn) {
        toast.error('Check in time is required');
        return;
      }
      if (regularizationFor !== 'Check In' && !regularizationCheckOut) {
        toast.error('Check out time is required');
        return;
      }
    }

    try {
      const employeeId = selectedRow?.employeeId || currentEmployee?.id;
      if (!employeeId) {
        toast.error('Employee not found for regularization request');
        return;
      }

      const checkInTime = regularizationFor === 'Check Out'
        ? null
        : toTwentyFourHourTime(regularizationCheckIn, regularizationCheckInMeridiem);
      const checkOutTime = regularizationFor === 'Check In'
        ? null
        : toTwentyFourHourTime(regularizationCheckOut, regularizationCheckOutMeridiem);

      if (regularizationRequestType !== 'Overtime' && regularizationFor !== 'Check Out' && !checkInTime) {
        toast.error('Enter check in time in hh:mm format and select AM/PM');
        return;
      }
      if (regularizationRequestType !== 'Overtime' && regularizationFor !== 'Check In' && !checkOutTime) {
        toast.error('Enter check out time in hh:mm format and select AM/PM');
        return;
      }

      const toDateTime = (date: string, time: string) => `${date}T${time}:00`;

      await attendanceApi.submitRegularizationRequest({
        employeeId,
        attendanceId: selectedRow?.attendanceId,
        date: regularizationDate,
        requestType: regularizationRequestType,
        requestedCheckIn: regularizationRequestType === 'Overtime'
          ? null
          : (checkInTime ? toDateTime(regularizationDate, checkInTime) : null),
        requestedCheckOut: regularizationRequestType === 'Overtime'
          ? null
          : (checkOutTime ? toDateTime(regularizationDate, checkOutTime) : null),
        requestedOtHours: regularizationRequestType === 'Overtime' ? Number(regularizationOtHours) : null,
        reason: regularizationReason.trim(),
      });
      toast.success(`${regularizationRequestType} request submitted successfully`);
      setRegularizationOpen(false);
      // Refresh data to reflect updated request status
      handleGenerate();
    } catch (err: any) {
      const apiError = err?.response?.data;
      const modelErrors = apiError?.errors && typeof apiError.errors === 'object'
        ? Object.values(apiError.errors).flat().join(' ')
        : '';
      toast.error(apiError?.message || apiError?.title || modelErrors || 'Failed to submit regularization request');
    }
  };

  const detailPunchTimes = useMemo(
    () => derivePunchTimes(punches, selectedRow?.date ? normalizeDateKey(selectedRow.date) : localFromDate || undefined),
    [punches, selectedRow, localFromDate]
  );

//   const isSelectedRowAbsent = (selectedRow?.attendanceStatus || '').toLowerCase().includes('absent');
//   const detailCheckInTime = isSelectedRowAbsent
//   ? ''
//   : formatAttendanceTime(selectedAttendance?.checkInTime)
//     || formatAttendanceTime(selectedRow?.checkInTime)
//     || detailPunchTimes.checkIn;

// const detailCheckOutTime = isSelectedRowAbsent
//   ? ''
//   : formatAttendanceTime(selectedAttendance?.checkOutTime)
//     || formatAttendanceTime(selectedRow?.checkOutTime)
//     || detailPunchTimes.checkOut;

    const detailCheckInTime =
      formatAttendanceTime(selectedRow?.checkInTime) ||
      formatAttendanceTime(selectedAttendance?.checkInTime) ||
      detailPunchTimes.checkIn ||
      '';

    const detailCheckOutTime =
      formatAttendanceTime(selectedRow?.checkOutTime) ||
      formatAttendanceTime(selectedAttendance?.checkOutTime) ||
      detailPunchTimes.checkOut ||
      '';

  const detailWorkedHours = selectedAttendance?.totalHours !== undefined && selectedAttendance?.totalHours !== null
    ? formatWorkedHours(selectedAttendance.totalHours)
    : formatWorkedHours(selectedRow?.workedHours);
  const requestRows = useMemo(() => {
    // Only show rows needing regularization (from backend flag)
    const regularizableRows = rows.filter((row) => row.needsRegularization);

    if (requestViewFilter === 'Attendance') {
      return regularizableRows.filter((row) => canRequestAttendance(row));
    }

    if (requestViewFilter === 'Overtime') {
      return regularizableRows.filter((row) => canRequestOvertime(row));
    }

    if (requestViewFilter === 'CompOff') {
      return regularizableRows.filter((row) => canRequestCompOff(row));
    }

    return regularizableRows;
  }, [requestViewFilter, rows]);

  if (!isEmbeddedMode) {
    return (
      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Attendance Regularization Request
            </Typography>

            {localError === null && localFromDate && localToDate && localFromDate > localToDate && (
              <Alert severity="error" sx={{ mb: 2 }}>
                From Date cannot be after To Date.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: employeeJoinedDate || undefined }}
                value={localFromDate}
                onChange={(e) => setLocalFromDate(e.target.value)}
              />

             <TextField
                label="To Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: employeeJoinedDate || undefined,
                  max: today   // 👈 prevents future date selection
                }}
                value={localToDate}
                onChange={(e) => setLocalToDate(e.target.value)}
              />

              {canRegularizeForEmployees ? (
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 280 }}
                  options={adminEmployees}
                  getOptionLabel={(option) =>
                    `${option.name}${option.employeeCode ? ` (${option.employeeCode})` : ""}`
                  }
                  value={
                    adminEmployees.find((emp) => emp.id === selectedEmployeeId) || null
                  }
                  onChange={(_, newValue) => {
                    setSelectedEmployeeId(newValue?.id || "");
                    setRows([]);
                    setSummary(defaultSummary);
                    setGenerated(false);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" />
                  )}
                  noOptionsText="No employees found"
                />
              ) : (
                <TextField
                  label="Employee"
                  size="small"
                  value={currentEmployee?.label || ""}
                  disabled
                  sx={{ minWidth: 260 }}
                />
              )}

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={localStatus} label="Status" onChange={(e) => setLocalStatus(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {effectiveStatusOptions.map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                startIcon={localLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                onClick={handleGenerate}
                disabled={
                  localLoading ||
                  (canRegularizeForEmployees && !selectedEmployeeId) ||
                  Boolean(localFromDate && localToDate && localFromDate > localToDate)
                }
                sx={{ textTransform: 'none' }}
              >
                {localLoading ? 'Getting...' : 'Get Data'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
            {localError}
          </Alert>
        )}

        {!localLoading && generated && (
          <>
            <Tabs
              value={requestViewFilter}
              onChange={(_, value) => setRequestViewFilter(value)}
              sx={{ mb: 2 }}
            >
              <Tab value="All" label={`All (${rows.filter((r) => r.needsRegularization).length})`} />
              <Tab value="Attendance" label={`Attendance (${rows.filter((r) => r.needsRegularization && canRequestAttendance(r)).length})`} />
              <Tab value="Overtime" label={`OT (${rows.filter((r) => r.needsRegularization && canRequestOvertime(r)).length})`} />
              <Tab value="CompOff" label={`Comp-Off (${rows.filter((r) => r.needsRegularization && canRequestCompOff(r)).length})`} />
            </Tabs>

            {/* <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card><CardContent><Typography variant="body2">Present</Typography><Typography variant="h5">{summary.presentCount}</Typography></CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card><CardContent><Typography variant="body2">Absent</Typography><Typography variant="h5">{summary.absentCount}</Typography></CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card><CardContent><Typography variant="body2">Leave</Typography><Typography variant="h5">{summary.leaveCount}</Typography></CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card><CardContent><Typography variant="body2">Late</Typography><Typography variant="h5">{summary.lateCount}</Typography></CardContent></Card>
              </Grid>
            </Grid> */}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Employee Code</strong></TableCell>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell><strong>Request Status</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No records requiring regularization.</TableCell>
                    </TableRow>
                  ) : (
                    requestRows.map((row, idx) => {
                      const deviationReasons: string[] = [];
                      if (row.isLateCheckIn) deviationReasons.push('Late Check-In');
                      if (row.isEarlyCheckOut) deviationReasons.push('Early Check-Out');
                      if (row.isMissingPunch) deviationReasons.push('Missing Punch');
                      if ((row.attendanceStatus || '').toLowerCase().includes('absent')) deviationReasons.push('Absent');
                      if (row.attendanceStatus === 'Half Day Leave') deviationReasons.push('Half-Day Leave, other half not worked');
                      else if ((row.attendanceStatus || '').toLowerCase().includes('half')) deviationReasons.push('Half-Day');
                      if (row.isCompOffEligible) deviationReasons.push('Worked on Holiday/Off');
                      if (row.isOvertimeEligible) deviationReasons.push(`Overtime Eligible${row.overtimeHours ? ` (${row.overtimeHours}h)` : ''}`);

                      const attRegStatus = row.attendanceRegularizationStatus || '';
                      const otRegStatus = row.overtimeRequestStatus || '';
                      const compOffRegStatus = row.compOffRequestStatus || '';

                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{formatAttendanceDate(row.date)}</TableCell>
                          <TableCell>{row.employeeCode}</TableCell>
                          <TableCell>{row.employeeName}</TableCell>
                          <TableCell>
                            <Chip label={row.attendanceStatus || 'Absent'} color={getStatusChipColor(row.attendanceStatus || '')} size="small" />
                          </TableCell>
                          <TableCell>
                            {deviationReasons.length > 0 ? deviationReasons.join(', ') : '-'}
                          </TableCell>
                          <TableCell>
                            {attRegStatus && (
                              <Chip
                                label={`Att: ${attRegStatus}`}
                                size="small"
                                color={attRegStatus === 'Approved' ? 'success' : attRegStatus === 'Rejected' ? 'error' : 'warning'}
                                sx={{ mr: 0.5 }}
                              />
                            )}
                            {otRegStatus && (
                              <Chip
                                label={`OT: ${otRegStatus}`}
                                size="small"
                                color={otRegStatus === 'Approved' ? 'success' : otRegStatus === 'Rejected' ? 'error' : 'warning'}
                              />
                            )}
                            {compOffRegStatus && (
                              <Chip
                                label={`Comp-Off: ${compOffRegStatus}`}
                                size="small"
                                color={compOffRegStatus === 'Approved' ? 'success' : compOffRegStatus === 'Rejected' ? 'error' : 'warning'}
                                sx={{ ml: 0.5 }}
                              />
                            )}
                            {!attRegStatus && !otRegStatus && !compOffRegStatus && '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton color="primary" size="small" onClick={() => handleOpenDetails(row)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canRequestAttendance(row) && !['Pending', 'Approved'].includes(attRegStatus.trim()) && (
                              <Tooltip title="Add Regularization">
                                <IconButton color="success" size="small" onClick={() => handleOpenRegularization(row, 'Attendance')}>
                                  <Add fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canRequestOvertime(row) && (
                              <Tooltip title={otRegStatus ? `OT: ${otRegStatus}` : 'Apply OT'}>
                                <span>
                                  <IconButton
                                    color="warning"
                                    size="small"
                                    onClick={() => handleOpenRegularization(row, 'Overtime')}
                                    disabled={['Pending', 'Approved'].includes(otRegStatus.trim())}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canRequestCompOff(row) && (
                              <Tooltip title={compOffRegStatus ? `Comp-Off: ${compOffRegStatus}` : 'Apply Comp-Off'}>
                                <span>
                                  <IconButton
                                    color="info"
                                    size="small"
                                    onClick={() => handleOpenCompOff(row)}
                                    disabled={['Pending', 'Approved'].includes(compOffRegStatus.trim())}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>Attendance Details</DialogTitle>
              <DialogContent>
                <Tabs value={detailTab} onChange={(_, value) => setDetailTab(value)} sx={{ mb: 2 }}>
                  <Tab label="Details" />
                  <Tab label="Punches" />
                </Tabs>

                {detailTab === 0 && selectedRow && (
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Date:</strong> {formatAttendanceDate(selectedRow.date)}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Employee Code:</strong> {selectedRow.employeeCode}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Employee Name:</strong> {selectedRow.employeeName}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Worked Hours:</strong> {detailWorkedHours}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Actual Checkin Time:</strong> {detailCheckInTime}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Actual Checkout Time:</strong> {detailCheckOutTime}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Attendance Status:</strong> {selectedRow.attendanceStatus || 'Absent'}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Eligible OT Hours:</strong> {selectedRow.overtimeHours || 0}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>OT Request Status:</strong> {selectedRow.overtimeRequestStatus || '-'}</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Typography variant="body2"><strong>Att. Regularization Status:</strong> {selectedRow.attendanceRegularizationStatus || '-'}</Typography></Grid>
                    {selectedRow.isLateCheckIn && <Grid size={{ xs: 12, sm: 6 }}><Chip label="Late Check-In" color="warning" size="small" /></Grid>}
                    {selectedRow.isEarlyCheckOut && <Grid size={{ xs: 12, sm: 6 }}><Chip label="Early Check-Out" color="warning" size="small" /></Grid>}
                    {selectedRow.isMissingPunch && <Grid size={{ xs: 12, sm: 6 }}><Chip label="Missing Punch" color="error" size="small" /></Grid>}
                    {selectedRow.isHoliday && <Grid size={{ xs: 12, sm: 6 }}><Chip label="Holiday" color="info" size="small" /></Grid>}
                    {selectedRow.isCompOffEligible && <Grid size={{ xs: 12, sm: 6 }}><Chip label="Comp-Off Eligible" color="success" size="small" /></Grid>}
                  </Grid>
                )}

                {detailTab === 1 && (
                    <>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Showing punches for {selectedRow?.date ? formatAttendanceDate(selectedRow.date) : '-'}.
                    </Typography>

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Mode of Entry</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {punchesLoading ? (
                          <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                        ) : punches.length === 0 ? (
                          <TableRow><TableCell colSpan={4} align="center">No punches found for selected date.</TableCell></TableRow>
                        ) : (
                          punches.map((p, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{p.date || '-'}</TableCell>
                              <TableCell>{p.time || '-'}</TableCell>
                              <TableCell>{p.type || '-'}</TableCell>
                              <TableCell>{p.modeOfEntry || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDetailsOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={regularizationOpen} onClose={() => setRegularizationOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>New Request</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Stack spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Request Type</InputLabel>
                        <Select
                          value={regularizationRequestType}
                          label="Request Type"
                          onChange={(e) => setRegularizationRequestType(e.target.value as 'Attendance' | 'Overtime')}
                          disabled={selectedRequestOptions.length === 1}
                        >
                          {selectedRequestOptions.includes('Attendance') && <MenuItem value="Attendance">Attendance</MenuItem>}
                          {selectedRequestOptions.includes('Overtime') && <MenuItem value="Overtime">OT</MenuItem>}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Regularization request for</InputLabel>
                        <Select
                          value={regularizationFor}
                          label="Regularization request for"
                          onChange={(e) => setRegularizationFor(e.target.value as 'Both' | 'Check In' | 'Check Out')}
                          disabled={regularizationRequestType === 'Overtime'}
                        >
                          <MenuItem value="Both">Both</MenuItem>
                          <MenuItem value="Check In">Check In</MenuItem>
                          <MenuItem value="Check Out">Check Out</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Reason"
                        multiline
                        minRows={3}
                        value={regularizationReason}
                        onChange={(e) => setRegularizationReason(e.target.value)}
                      />

                      <TextField
                        label="Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: employeeJoinedDate || undefined }}
                        value={regularizationDate}
                        onChange={(e) => setRegularizationDate(e.target.value)}
                        disabled
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Checkin Time"
                          placeholder="hh:mm"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ inputMode: 'numeric', maxLength: 5, pattern: '\\d{2}:\\d{2}' }}
                          value={regularizationCheckIn}
                          onChange={(e) => setRegularizationCheckIn(normalizeMeridiemTimeInput(e.target.value))}
                          disabled={regularizationRequestType === 'Overtime' || regularizationFor === 'Check Out'}
                          sx={{ flex: 1 }}
                        />
                        <FormControl
                          size="small"
                          disabled={regularizationRequestType === 'Overtime' || regularizationFor === 'Check Out'}
                          sx={{ width: 110 }}
                        >
                          <InputLabel>AM/PM</InputLabel>
                          <Select
                            value={regularizationCheckInMeridiem}
                            label="AM/PM"
                            onChange={(e) => setRegularizationCheckInMeridiem(e.target.value as Meridiem)}
                          >
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Checkout Time"
                          placeholder="hh:mm"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ inputMode: 'numeric', maxLength: 5, pattern: '\\d{2}:\\d{2}' }}
                          value={regularizationCheckOut}
                          onChange={(e) => setRegularizationCheckOut(normalizeMeridiemTimeInput(e.target.value))}
                          disabled={regularizationRequestType === 'Overtime' || regularizationFor === 'Check In'}
                          sx={{ flex: 1 }}
                        />
                        <FormControl
                          size="small"
                          disabled={regularizationRequestType === 'Overtime' || regularizationFor === 'Check In'}
                          sx={{ width: 110 }}
                        >
                          <InputLabel>AM/PM</InputLabel>
                          <Select
                            value={regularizationCheckOutMeridiem}
                            label="AM/PM"
                            onChange={(e) => setRegularizationCheckOutMeridiem(e.target.value as Meridiem)}
                          >
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <TextField
                        label="OT Hours"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: 0.25 }}
                        value={regularizationOtHours}
                        onChange={(e) => setRegularizationOtHours(e.target.value)}
                        disabled={regularizationRequestType !== 'Overtime'}
                      />
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, md: 5 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Attendance Details</Typography>
                        <Typography variant="body2"><strong>Date:</strong> {selectedRow?.date ? formatAttendanceDate(selectedRow.date) : '-'}</Typography>
                        <Typography variant="body2"><strong>Employee Code:</strong> {selectedRow?.employeeCode || '-'}</Typography>
                        <Typography variant="body2"><strong>Employee Name:</strong> {selectedRow?.employeeName || '-'}</Typography>
                        <Typography variant="body2"><strong>Actual Checkin Time:</strong> {detailCheckInTime}</Typography>
                        <Typography variant="body2"><strong>Actual Checkout Time:</strong> {detailCheckOutTime}</Typography>
                        <Typography variant="body2"><strong>Worked Hours:</strong> {detailWorkedHours}</Typography>
                        <Typography variant="body2"><strong>Attendance Status:</strong> {selectedRow?.attendanceStatus || 'Absent'}</Typography>
                        <Typography variant="body2"><strong>Eligible OT Hours:</strong> {selectedRow?.overtimeHours || 0}</Typography>
                        <Typography variant="body2"><strong>OT Request Status:</strong> {selectedRow?.overtimeRequestStatus || '-'}</Typography>
                        <Typography variant="body2"><strong>Att. Regularization Status:</strong> {selectedRow?.attendanceRegularizationStatus || '-'}</Typography>
                        {selectedRow?.isLateCheckIn && <Chip label="Late Check-In" color="warning" size="small" sx={{ mt: 0.5, mr: 0.5 }} />}
                        {selectedRow?.isEarlyCheckOut && <Chip label="Early Check-Out" color="warning" size="small" sx={{ mt: 0.5, mr: 0.5 }} />}
                        {selectedRow?.isMissingPunch && <Chip label="Missing Punch" color="error" size="small" sx={{ mt: 0.5, mr: 0.5 }} />}
                        {selectedRow?.isCompOffEligible && <Chip label="Comp-Off Eligible" color="success" size="small" sx={{ mt: 0.5 }} />}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRegularizationOpen(false)}>Close</Button>
                <Button variant="contained" onClick={handleSubmitRegularization}>Submit</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={compOffOpen} onClose={() => setCompOffOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>Apply Comp-Off</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {compOffRow
                    ? `Worked on ${formatAttendanceDate(compOffRow.date)} (holiday / week-off). A comp-off credit will be added on approval.`
                    : ''}
                </Typography>
                <TextField
                  label="Reason"
                  fullWidth
                  multiline
                  minRows={3}
                  value={compOffReason}
                  onChange={(e) => setCompOffReason(e.target.value)}
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCompOffOpen(false)}>Close</Button>
                <Button variant="contained" onClick={handleSubmitCompOff} disabled={compOffSubmitting}>
                  {compOffSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Attendance Filters
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={singleDate || ''}
            onChange={(e) => onSingleDateChange?.(e.target.value)}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={department || ''}
              label="Department"
              onChange={(e) => onDepartmentChange?.(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {(departments || []).map((d) => (
                <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              value={employee || ''}
              label="Employee"
              disabled={lockEmployee}
              onChange={(e) => onEmployeeChange?.(e.target.value)}
            >
              {!lockEmployee && <MenuItem value="">All</MenuItem>}
              {(employees || []).map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>{emp.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status || ''}
              label="Status"
              onChange={(e) => onStatusChange?.(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {effectiveStatusOptions.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
            onClick={() => onGetData?.()}
            disabled={loading}
          >
            {loading ? 'Getting...' : 'Get Data'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AttendanceReportFilterForm;

